import { motion, AnimatePresence } from 'framer-motion';
import { X, User, Camera, MapPin, FileText, Lock, Unlock } from 'lucide-react';
import { Button } from '../ui/Button';
import { Progress } from '../ui/Progress';
import { useAuth } from '../../hooks/useAuth';

const ProfileCompletionModal = ({ isOpen, onClose, onCompleteProfile }) => {
  const { user, getProfileCompletion, getMissingProfileFields } = useAuth();
  
  const completion = getProfileCompletion();
  const missingFields = getMissingProfileFields();
  
  const requiredFields = [
    { name: 'Full Name', icon: User, completed: !missingFields.includes('Full Name'), weight: 35 },
    { name: 'Profile Photo', icon: Camera, completed: !missingFields.includes('Profile Photo'), weight: 30 },
    { name: 'Bio', icon: FileText, completed: !missingFields.includes('Bio'), weight: 20 },
    { name: 'Country', icon: MapPin, completed: !missingFields.includes('Country'), weight: 15 }
  ];
  
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary-teal/10 rounded-full flex items-center justify-center">
                  <Lock className="w-5 h-5 text-primary-teal" />
                </div>
                <h2 className="text-xl font-bold text-neutral-900">Complete Your Profile</h2>
              </div>
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-full bg-neutral-100 hover:bg-neutral-200 flex items-center justify-center transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            
            {/* Content */}
            <div className="p-6">
              <div className="text-center mb-6">
                <p className="text-neutral-600 mb-4">
                  To create projects on CrowBlock, you need to complete your profile. This helps build trust with potential supporters.
                </p>
                
                {/* Progress */}
                <div className="bg-neutral-50 rounded-xl p-4 mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-neutral-700">Progress</span>
                    <span className="text-lg font-bold text-primary-teal">{completion}%</span>
                  </div>
                  <Progress value={completion} className="h-3" />
                  <p className="text-xs text-neutral-500 mt-2">
                    {80 - completion}% more to unlock project creation
                  </p>
                </div>
              </div>
              
              {/* Required Fields */}
              <div className="space-y-3 mb-6">
                <h3 className="text-sm font-semibold text-neutral-900 mb-3">Required Information</h3>
                {requiredFields.map((field) => (
                  <div
                    key={field.name}
                    className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-colors ${
                      field.completed
                        ? 'border-green-200 bg-green-50'
                        : 'border-neutral-200 bg-neutral-50'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      field.completed ? 'bg-green-100' : 'bg-neutral-200'
                    }`}>
                      {field.completed ? (
                        <Unlock className="w-4 h-4 text-green-600" />
                      ) : (
                        <field.icon className="w-4 h-4 text-neutral-500" />
                      )}
                    </div>
                    <div className="flex-1">
                      <span className={`font-medium ${
                        field.completed ? 'text-green-900' : 'text-neutral-700'
                      }`}>
                        {field.name}
                      </span>
                      <div className="text-xs text-neutral-500">
                        {field.weight}% of profile completion
                      </div>
                    </div>
                    {field.completed && (
                      <div className="text-green-600 text-xs font-medium">✓ Complete</div>
                    )}
                  </div>
                ))}
              </div>
              
              {/* Actions */}
              <div className="space-y-3">
                <Button
                  onClick={onCompleteProfile}
                  className="w-full bg-primary-teal hover:bg-primary-teal/90"
                  size="lg"
                >
                  Complete Profile Now
                </Button>
                <Button
                  onClick={onClose}
                  variant="outline"
                  className="w-full"
                  size="lg"
                >
                  Maybe Later
                </Button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ProfileCompletionModal;